/**
 * Catalyst Frontend SDK
 *
 * Lightweight, production-ready error tracking and session monitoring.
 * Features:
 * - Immediate error reporting
 * - Rate limiting (max 10 errors/min)
 * - Deduplication via fingerprinting
 * - Breadcrumb collection (UI, API, Console)
 * - Session crash-free rate tracking
 */

interface CatalystConfig {
    apiKey?: string;
    environment?: string;
    appVersion?: string;
    endpoint?: string;
}

interface Breadcrumb {
    timestamp: string;
    message: string;
    category?: string;
    data?: any;
}

class CatalystClient {
    private config: CatalystConfig = {};
    private sessionId: string;
    private breadcrumbs: Breadcrumb[] = [];
    private maxBreadcrumbs = 20;
    private initialized = false;

    private seenFingerprints: Set<string> = new Set();

    // Rate limiting (max 10 errors per minute)
    private rateLimitWindowMs = 60 * 1000;
    private errorCountInWindow = 0;
    private windowStart = Date.now();
    private maxErrorsPerWindow = 10;

    constructor() {
        this.sessionId = crypto.randomUUID();
    }

    public init(config: CatalystConfig = {}) {
        const env = (import.meta as any).env || {};

        // Build the base API URL, matching how api.ts does it
        const apiBase = config.endpoint || env.VITE_API_BASE_URL || '/api';

        this.config = {
            apiKey: config.apiKey || env.VITE_CATALYST_API_KEY,
            environment: config.environment || env.VITE_CATALYST_ENV || 'PRODUCTION',
            appVersion: config.appVersion || env.VITE_VERSION || '1.0.0',
            endpoint: `${apiBase}/catalyst/ingest`
        };

        console.log('[Catalyst] Initializing with endpoint:', this.config.endpoint);
        console.log('[Catalyst] API Key present:', !!this.config.apiKey);

        if (!this.config.apiKey) {
            console.warn('[Catalyst] Disabled: No API Key provided via VITE_CATALYST_API_KEY');
            return;
        }

        this.initialized = true;
        this.setupGlobalHandlers();
        this.startSession();

        this.addBreadcrumb('Catalyst initialized', 'system');
        console.log('[Catalyst] ✅ SDK initialized successfully. Session:', this.sessionId);
    }

    private setupGlobalHandlers() {
        // Handle standard unhandled exceptions
        window.addEventListener('error', (event) => {
            console.log('[Catalyst] Caught window.onerror:', event.message);
            this.reportError(event.error || new Error(event.message), {
                errorType: 'UncaughtException',
                screenName: window.location.pathname
            });
        });

        // Handle unhandled Promise rejections (async/await)
        window.addEventListener('unhandledrejection', (event) => {
            console.log('[Catalyst] Caught unhandledrejection:', event.reason);
            this.reportError(event.reason || new Error('Unhandled Promise Rejection'), {
                errorType: 'UnhandledRejection',
                screenName: window.location.pathname
            });
        });
    }

    public addBreadcrumb(message: string, category: string = 'info', data?: any) {
        this.breadcrumbs.push({
            timestamp: new Date().toISOString(),
            message,
            category,
            data
        });

        if (this.breadcrumbs.length > this.maxBreadcrumbs) {
            this.breadcrumbs.shift();
        }
    }

    private checkRateLimit(): boolean {
        const now = Date.now();
        if (now - this.windowStart >= this.rateLimitWindowMs) {
            this.windowStart = now;
            this.errorCountInWindow = 0;
        }

        if (this.errorCountInWindow >= this.maxErrorsPerWindow) {
            return false;
        }

        this.errorCountInWindow++;
        return true;
    }

    private generateFingerprint(errorType: string, message: string): string {
        return `${errorType}|${message}`;
    }

    public reportError(error: any, context: Record<string, any> = {}) {
        if (!this.initialized || !this.config.apiKey) {
            console.warn('[Catalyst] reportError called but SDK not initialized or no API key');
            return;
        }

        const errorObj = error instanceof Error ? error : new Error(String(error));
        const errorType = context.errorType || errorObj.name || 'Error';
        const errorMessage = errorObj.message || 'Unknown error';
        const stackTrace = errorObj.stack || '';

        // Deduplication
        const fp = this.generateFingerprint(errorType, errorMessage);
        if (this.seenFingerprints.has(fp)) {
            console.log('[Catalyst] Duplicate error, skipping:', errorMessage);
            return;
        }

        if (!this.checkRateLimit()) {
            console.warn('[Catalyst] Rate limit exceeded, dropping crash report.');
            return;
        }

        this.seenFingerprints.add(fp);

        const payload = {
            errorType,
            errorMessage,
            stackTrace,
            environment: this.config.environment,
            appVersion: this.config.appVersion,
            platform: 'WEB',
            osVersion: navigator.userAgent,
            locale: navigator.language,
            sessionId: this.sessionId,
            breadcrumbs: [...this.breadcrumbs],
            severity: context.severity || 'ERROR',
            screenName: context.screenName || window.location.pathname,
            customData: context.customData || {}
        };

        console.log('[Catalyst] 🚨 Reporting error:', errorType, '-', errorMessage);

        // Send immediately — no batching delay
        this.sendCrashReport(payload);
    }

    private async sendCrashReport(payload: any) {
        const url = `${this.config.endpoint}/crash`;
        console.log('[Catalyst] Sending crash to:', url);

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.config.apiKey}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                console.log('[Catalyst] ✅ Crash report sent successfully:', data);
            } else {
                const text = await response.text();
                console.error('[Catalyst] ❌ Server rejected crash report:', response.status, text);
            }
        } catch (e) {
            console.error('[Catalyst] ❌ Network error sending crash report:', e);
        }
    }

    private async startSession() {
        if (!this.config.apiKey) return;

        const url = `${this.config.endpoint}/session`;
        console.log('[Catalyst] Starting session at:', url);

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.config.apiKey}`
                },
                body: JSON.stringify({
                    sessionId: this.sessionId,
                    appVersion: this.config.appVersion,
                    platform: 'WEB'
                })
            });

            if (response.ok) {
                console.log('[Catalyst] ✅ Session started successfully');
            } else {
                const text = await response.text();
                console.error('[Catalyst] ❌ Session start failed:', response.status, text);
            }
        } catch (e) {
            console.warn('[Catalyst] Failed to start session:', e);
        }
    }
}

// Export singleton instance
export const catalyst = new CatalystClient();

// Helpful shortcuts
export const initCatalyst = (config?: CatalystConfig) => catalyst.init(config || {});
export const reportError = (error: any, context?: any) => catalyst.reportError(error, context);
export const addBreadcrumb = (message: string, category?: string, data?: any) => catalyst.addBreadcrumb(message, category, data);
